import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { SolIcon } from '@sol/icons';
import { AbstractComponent } from '../../abstract.component';
import { SolBannerComponent } from '../sol-banner/sol-banner.component';
import { SolButtonComponent } from '../sol-button/sol-button.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolButtonType } from '../../../models/core/sol-button';
import { BrowserTools } from '../../../tools/browser-tools';
import { BannerEntry, BannerService } from '../../../services/banner.service';
import { SolBannerHostViewState } from './sol-banner-host.state';

/**
 * Renders the published app-level banners one at a time, with the navigation for moving through the rest. The list
 * itself stays in `BannerService`: a publisher hands over the whole set and the host pages through it.
 *
 * An arrow disables on the entry the press lands on, so focus is handed to the opposite arrow rather than
 * left on a control the browser has just taken out of the tab order — and rather than falling back to the
 * document, which is where refusing the handover left whoever was standing on it.
 */
@Component({
  selector: 'sol-banner-host',
  imports: [SolBannerComponent, SolButtonComponent, TranslatePipe],
  templateUrl: './sol-banner-host.component.html',
  host: { '[class]': 'hostClass$()' },
})
export class SolBannerHostComponent extends AbstractComponent<SolBannerHostViewState> {
  // Static references
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly bannerHostService = inject(BannerService);

  // Computed
  protected readonly current$ = this.compute(state => state.current);
  protected readonly paged$ = this.compute(state => state.paged);
  protected readonly atFirst$ = this.compute(state => state.atFirst);
  protected readonly atLast$ = this.compute(state => state.atLast);
  protected readonly dots$ = this.compute(state => state.dots);
  protected readonly position$ = this.compute(state => state.position);
  protected readonly dotsStyle$ = this.compute(state => state.dotsStyle);
  protected readonly hostClass$ = this.compute(state => state.hostClass);

  // Elements
  @ViewChild('previousArrow', { read: ElementRef }) private previousArrow?: ElementRef<HTMLElement>;
  @ViewChild('nextArrow', { read: ElementRef }) private nextArrow?: ElementRef<HTMLElement>;

  constructor() {
    super(SolBannerHostViewState.of());
    this.bannerHostService.watchComputed(
      serviceState => serviceState.appEntries,
      entries => this.update(state => state.withEntries(entries)),
    );
    this.bannerHostService.watchComputed(
      serviceState => serviceState.placement,
      placement => this.update(state => state.withPlacement(placement)),
    );
  }

  protected onVisibleChange(entry: BannerEntry): void {
    this.bannerHostService.close(entry.owner, entry.key);
  }

  protected onActionPress(entry: BannerEntry): void {
    this.bannerHostService.pressAction(entry.owner, entry.key);
  }

  protected onPrevious(): void {
    const held = BrowserTools.holdsFocus(this.previousArrow);
    this.update(state => state.withIndex(state.index - 1));
    BrowserTools.handOverFocus(held && this.atFirst$(), this.nextArrow);
  }

  protected onNext(): void {
    const held = BrowserTools.holdsFocus(this.nextArrow);
    this.update(state => state.withIndex(state.index + 1));
    BrowserTools.handOverFocus(held && this.atLast$(), this.previousArrow);
  }
}
